import type { Dispatch, SetStateAction } from "react";
import { ADR_CLASSES } from "../constants";
import { DepartmentFieldAccess } from "../departmentFields";
import type { ExtractFillMap } from "../extractFill";
import { BoundedDate } from "../boundedDate";
import { todayISODate } from "../formModel";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import { ServiceSelectionMapper } from "../serviceActive";
import { TemperatureValue } from "../temperatureValue";
import type { DepartmentConfig, FormState, ServiceKey } from "../types";
import { timePhaseStarted, type Errors } from "../validation";
import { AttachmentFields } from "./AttachmentFields";
import { Checkbox, SelectInput, TextArea, TextInput } from "./fields";
import { ServiceCard } from "./ServiceCard";
import { ServicePills } from "./ServicePills";
import { TimeWindowFields } from "./TimeWindowFields";

const serviceSelectionMapper = new ServiceSelectionMapper();

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  errors: Errors;
  extractFill?: ExtractFillMap;
  config: DepartmentConfig;
}

export function AdditionalServices({ form, setForm, errors, extractFill, config }: Props) {
  const fields = new DepartmentFieldAccess(config);
  const copy = useQuoteCopy();
  const toggle = (key: ServiceKey, active: boolean) => {
    if (key === "ref" && fields.requiresReferences() && !active) return;
    setForm((f) => serviceSelectionMapper.update(f, key, active));
  };
  const pillLabels: Partial<Record<ServiceKey, string>> = {
    ref: copy.servicePill("ref", fields.hasExtendedReferences()),
  };

  return (
    <div className="nv-svc">
      <ServicePills
        form={form}
        onToggle={toggle}
        labels={pillLabels}
        keys={fields.visibleServiceKeys()}
        locked={fields.lockedServiceKeys()}
      />
      <div className="nv-svc-grid">
        <ServiceCard
          id="nv-svc-adr"
          title={copy.servicePill("adr")}
          active={form.adr.active}
        >
          <div className="nv-svc-stack">
            <SelectInput
              id="nv-adr-class"
              label={copy.t("services.adrClass")}
              error={errors.adrClass}
              fillStatus={extractFill?.status("adrClass")}
              value={form.adr.adrClass}
              onChange={(v) => setForm((f) => ({ ...f, adr: { ...f.adr, adrClass: v } }))}
            >
              <option value="">{copy.t("services.selectClass")}</option>
              {ADR_CLASSES.map((c, index) => (
                <option key={c} value={c}>
                  {copy.adrClassLabel(index)}
                </option>
              ))}
            </SelectInput>
            <TextInput
              id="nv-adr-un"
              label={copy.t("services.unNumber")}
              placeholder={copy.t("services.unPlaceholder")}
              error={errors.adrUn}
              fillStatus={extractFill?.status("adrUn")}
              maxLength={4}
              inputMode="numeric"
              value={form.adr.un}
              onChange={(v) =>
                setForm((f) => ({ ...f, adr: { ...f.adr, un: v.replace(/\D/g, "").slice(0, 4) } }))
              }
            />
          </div>
        </ServiceCard>

        {fields.showsTemperature() && (
        <ServiceCard
          id="nv-svc-temp"
          title={copy.servicePill("temp")}
          active={form.temp.active}
        >
          <div className="nv-row">
            <TextInput
              id="nv-temp-min"
              label={copy.t("services.min")}
              className="nv-temp-field"
              type="number"
              step="0.01"
              placeholder="0"
              suffix="°C"
              required={form.temp.active}
              error={errors.tempMin}
              fillStatus={extractFill?.status("tempMin")}
              value={form.temp.min}
              onChange={(v) =>
                setForm((f) => ({ ...f, temp: { ...f.temp, min: TemperatureValue.capInput(v) } }))
              }
            />
            <TextInput
              id="nv-temp-max"
              label={copy.t("services.max")}
              className="nv-temp-field"
              type="number"
              step="0.01"
              placeholder="0"
              suffix="°C"
              required={form.temp.active}
              error={errors.tempMax}
              fillStatus={extractFill?.status("tempMax")}
              value={form.temp.max}
              onChange={(v) =>
                setForm((f) => ({ ...f, temp: { ...f.temp, max: TemperatureValue.capInput(v) } }))
              }
            />
          </div>
        </ServiceCard>
        )}

        <ServiceCard
          id="nv-svc-tail"
          title={copy.servicePill("tail")}
          active={form.tail.active}
        >
          <div className="nv-svc-stack">
            <Checkbox
              id="nv-tail-load"
              className="auto"
              checked={form.tail.load}
              onChange={(c) => setForm((f) => ({ ...f, tail: { ...f.tail, load: c } }))}
              label={copy.t("services.tailLoad")}
              fillStatus={extractFill?.status("tailLoad")}
              error={errors.tailLoad}
            />
            <Checkbox
              id="nv-tail-unload"
              className="auto"
              checked={form.tail.unload}
              onChange={(c) => setForm((f) => ({ ...f, tail: { ...f.tail, unload: c } }))}
              label={copy.t("services.tailUnload")}
              fillStatus={extractFill?.status("tailUnload")}
            />
          </div>
        </ServiceCard>

        <ServiceCard
          id="nv-svc-ftl"
          title={copy.servicePill("ftl")}
          active={form.ftl.active}
        >
          <p className="nv-svc-copy">{copy.t("services.ftlCopy")}</p>
        </ServiceCard>

        <ServiceCard
          id="nv-svc-ref"
          title={copy.servicePill("ref", fields.hasExtendedReferences())}
          active={form.ref.active || fields.requiresReferences()}
        >
          <TextInput
            id="nv-ref-reference"
            label={fields.hasExtendedReferences() ? copy.t("services.invoiceRef") : copy.t("services.reference")}
            required={fields.isRequired("reference")}
            error={errors.refReference}
            fillStatus={extractFill?.status("refReference")}
            value={form.ref.reference}
            onChange={(v) => setForm((f) => ({ ...f, ref: { ...f.ref, reference: v } }))}
          />
          {fields.hasExtendedReferences() && (
            <>
              <TextInput
                id="nv-ref-load"
                label={copy.t("services.loadingRef")}
                required={fields.isRequired("loadReference")}
                error={errors.refLoadReference}
                fillStatus={extractFill?.status("refLoadReference")}
                value={form.ref.loadReference}
                onChange={(v) => setForm((f) => ({ ...f, ref: { ...f.ref, loadReference: v } }))}
              />
              <TextInput
                id="nv-ref-unload"
                label={copy.t("services.unloadingRef")}
                required={fields.isRequired("unloadReference")}
                error={errors.refUnloadReference}
                fillStatus={extractFill?.status("refUnloadReference")}
                value={form.ref.unloadReference}
                onChange={(v) => setForm((f) => ({ ...f, ref: { ...f.ref, unloadReference: v } }))}
              />
            </>
          )}
          <TextArea
            id="nv-ref-notes"
            label={copy.t("services.notes")}
            rows={2}
            placeholder={copy.t("services.notesPlaceholder")}
            fillStatus={extractFill?.status("refNotes")}
            value={form.ref.notes}
            onChange={(v) => setForm((f) => ({ ...f, ref: { ...f.ref, notes: v } }))}
          />
        </ServiceCard>

        <ServiceCard
          id="nv-svc-attachments"
          title={copy.servicePill("attachments")}
          active={form.attachments.active}
        >
          <AttachmentFields
            files={form.attachments.files}
            error={errors.attachments}
            onChange={(files) => setForm((f) => ({ ...f, attachments: { ...f.attachments, files } }))}
          />
        </ServiceCard>

        <ServiceCard
          id="nv-svc-time"
          title={copy.servicePill("time")}
          active={form.time.active}
          className="nv-svc-span"
        >
          {fields.showsTimeDeliveryDeadline() && (
          <TextInput
            id="nv-time-deadline"
            label={copy.t("services.deadline")}
            className="nv-deadline-field"
            type="date"
            autoComplete="off"
            error={errors.deliveryDate}
            hint={copy.t("services.deliveryHint")}
            fillStatus={extractFill?.status("deliveryDate")}
            value={form.time.deliveryDate}
            min={form.pickupDate || todayISODate()}
            onChange={(v) =>
              setForm((f) => ({
                ...f,
                time: {
                  ...f.time,
                  deliveryDate: BoundedDate.capInput(v, f.pickupDate || todayISODate()),
                },
              }))
            }
            onBlur={() =>
              setForm((f) => ({
                ...f,
                time: {
                  ...f.time,
                  deliveryDate: BoundedDate.finishInput(
                    f.time.deliveryDate,
                    f.pickupDate || todayISODate(),
                  ),
                },
              }))
            }
          />
          )}
          <div className="nv-time-phases">
            <TimeWindowFields
              phase="load"
              time={form.time}
              setForm={setForm}
              errors={errors}
              required={timePhaseStarted(
                form.time.loadType,
                form.time.loadFrom,
                form.time.loadUntil,
                form.time.loadFixed,
              )}
              fillStatus={extractFill?.status("loadTime")}
            />
            <TimeWindowFields
              phase="unload"
              time={form.time}
              setForm={setForm}
              errors={errors}
              required={timePhaseStarted(
                form.time.unloadType,
                form.time.unloadFrom,
                form.time.unloadUntil,
                form.time.unloadFixed,
              )}
              fillStatus={extractFill?.status("unloadTime")}
            />
          </div>
        </ServiceCard>
      </div>
    </div>
  );
}
